// Parser dispatch by card name. Matches the card name (case-insensitive) against
// a priority-ordered list of bank keys.
//
// IMPORTANT: longer/more-specific keys must precede any key that is a substring
// of them (e.g. 'ADIB' before 'DIB', since 'ADIB'.includes('DIB') === true).
import { parseCBD } from './banks/cbd.js';
import { parseENBD } from './banks/enbd.js';
import { parseSIB } from './banks/sib.js';
import { parseEI } from './banks/ei.js';
import { parseADIB } from './banks/adib.js';
import { parseDIB } from './banks/dib.js';
import { parseFAB } from './banks/fab.js';
import { pageWith, fieldRight, amountWithSign } from './util.js';

// Labels banks use for the amount actually due this cycle (as opposed to the
// closing/current balance, which can include not-yet-due installments). A CR
// (credit) here means nothing is due.
const DUE_LABELS = [
  'Total Amount Due', 'Total Payment Due', 'Total Amount Payable',
  'Current Payment Due', 'Total Due Amount', 'Total Due',
];

// Best-effort, STRICT fallback: only accepts a currency-shaped value (optionally
// suffixed CR) on the same row as a due-amount label. Returns null when not
// confidently found, so callers fall back to the closing balance — no regression.
// Runs only when the bank parser didn't already set summary.totalPaymentDue.
function genericTotalPaymentDue(pages) {
  for (const label of DUE_LABELS) {
    const pg = pageWith(pages, label);
    if (!pg) continue;
    const raw = fieldRight(pg, label, { minX: 0, band: 6, want: /^[\d,]+\.\d{2}\s*(CR)?$/i });
    if (raw) {
      const { amount, credit } = amountWithSign(raw);
      if (amount != null) return credit ? -amount : amount;
    }
  }
  return null;
}

const PARSERS = [
  ['CBD', parseCBD],
  ['ENBD', parseENBD],
  ['SIB', parseSIB],
  ['EI', parseEI],
  ['ADIB', parseADIB],
  ['DIB', parseDIB],
  ['FAB', parseFAB],
];

// Returns the parse function for the given card name, or null if unknown. The
// returned function wraps the bank parser so every bank gets a CR-aware
// "Total Payment Due" via the generic fallback when it didn't extract one.
export function dispatchParser(cardName) {
  if (!cardName) return null;
  const upper = String(cardName).toUpperCase();
  const entry = PARSERS.find(([key]) => upper.includes(key));
  if (!entry) return null;
  const parser = entry[1];
  return (pages) => {
    const result = parser(pages);
    if (result && result.summary && result.summary.totalPaymentDue == null) {
      result.summary.totalPaymentDue = genericTotalPaymentDue(pages);
    }
    return result;
  };
}
